import { Modal, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { EMPTY_STATE_IMAGES } from '@shared/constants';
import { EditHideIcon, EyeIcon } from '@sketchnote/icons';
import { Header } from '@sketchnote/landing-page';
import { Button, FullPageLoader } from '@sketchnote/shared-components';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import { useState } from 'react';
import { PageErrorBoundary } from 'src/components/error-boundary/page-error-boundary';
import { ErrorCodes } from 'src/data/error-codes';
import { PublicBoard } from 'src/features/boards';
import { ChatView } from 'src/features/chat';
import { EmptyStateDashboard } from 'src/features/dashboard/components/EmptyState';
import { PublicDoc } from 'src/features/docs';
import { EmbedView } from 'src/features/Embed';
import { useKanbanStore } from 'src/features/kanban';
import SheetView from 'src/features/sheet/Worksheet';
import { WhiteBoard } from 'src/features/whiteboard/components';
import { ErrorContent } from 'src/pages/signup/signup.page';
import { useAuth } from 'src/shared/hooks/use-auth';

import { getFileType } from '..';
import { viewsWithoutFileHeader } from '../assets/data';
import { PublicFileViewNavbar } from '../components/PublicFileViewNavbar';
import { useFileStore } from '../store/useFileStore';

export const PublicFile = () => {
  const router = useRouter();
  const [password, setPassword] = useState('');
  const [incorrectPassword, setIncorrectPassword] = useState(false);
  const [publicFileStatus, setPublicFileStatus] = useState('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const fetchFile = useFileStore((s) => s.fetchFile);
  const onUpdatePublicFilePassword = useFileStore((s) => s.onUpdatePublicFilePassword);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
  const { userDetails, isPublicFile, publicPassword } = useAuth((s) => ({
    userDetails: s.userDetails,
    isPublicFile: s.isPublicFile,
    publicPassword: s.publicPassword,
  }));

  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );
  useEffect(() => {
    useAuth.setState({ isPublicFile: true });
    useKanbanStore.setState({ isPublicView: true, isReadOnly: true });
  }, [router?.query?.slug]);

  async function fetchCurrentFile() {
    const fileId = router.query.slug as string;
    const response: any = await fetchFile({ id: fileId, isPublic: true });

    if (response === ErrorCodes['FILE_ACCESS_DENIED']) {
      setPublicFileStatus('inactive');
    } else if (response === ErrorCodes['FILE_NOT_FOUND']) {
      setPublicFileStatus('unavailable');
    }
  }

  //Routes to the primary view when no query parameters are present
  useEffect(() => {
    if (
      (router.query.view === undefined ||
        router.query.view === 'undefined' ||
        router.query.viewId === undefined ||
        router.query.viewId === 'undefined') &&
      currentFile?.primaryFile?.id
    ) {
      const type = currentFile?.primaryFile?.type;
      const view = currentFile?.primaryFile?.view;

      router.replace({
        query: {
          ...router.query,
          view: currentFile?.primaryFile?.id,
          viewId: currentFile?.primaryFile?.id,
          type: getFileType({ type, view }),
        },
      });
    }
  }, [currentFile?.primaryFile?.id, router.query.view, router.query.viewId]);

  useEffect(() => {
    if (publicPassword) {
      fetchCurrentFile();
    }
  }, [publicPassword]);

  const handleUpdatePassword = async () => {
    setIsSubmitDisabled(true);
    const fileId = router.query.slug as string;
    const response: any = await onUpdatePublicFilePassword({ id: fileId, password });
    if (response === 'incorrect-password') {
      setIncorrectPassword(true);
      setIsSubmitDisabled(false);
    } else {
      useAuth.setState({ publicPassword: response });
      setIsSubmitDisabled(false);
      setIncorrectPassword(false);
    }
  };

  useEffect(() => {
    router?.query?.slug && fetchCurrentFile();
  }, [router?.query?.slug]);

  const activeInternalFile = useCallback(
    ({ id, type }: { id: string; type: string }) => {
      switch (type) {
        case 'document':
          return <PublicDoc docId={id} />;
        case 'whiteboard':
          return (
            <WhiteBoard
              id={id}
              memberId={userDetails?.memberInformation?.memberId || ''}
              isPublic={isPublicFile}
            />
          );
        case 'sheet':
          return <SheetView id={id} />;
        case 'embed':
          return <EmbedView id={id} />;
        case 'chat':
          return <ChatView id={id} isPublic={isPublicFile} />;
        default:
          return <PublicBoard />;
      }
    },
    [router.query]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [router.query.view]);

  return (
    <div>
      {(currentFile?.isLoading || !currentFile) &&
      !(publicFileStatus === 'inactive' || publicFileStatus === 'unavailable') ? (
        <FullPageLoader loading={currentFile?.isLoading || false} />
      ) : (
        <div>
          <Modal isOpen={currentFile?.isPublicFilePasswordSet || false} onClose={() => null}>
            <ModalOverlay />

            <ModalContent
              style={{
                maxWidth: '46rem',
                padding: '24px',
                marginTop: '20rem',
                borderRadius: '8px',
              }}
            >
              <p className="mb-32 text-xl text-gray-600">Please enter details to continue</p>
              <span className="mb-2 text-gray-600 text-xs block">Passcode</span>
              <div
                className={`${
                  incorrectPassword ? 'border border-red-500' : 'border'
                } w-full flex rounded-sm border bg-gray-100 mb-24 p-8`}
              >
                <input
                  onKeyDown={(e) => {
                    if (e.code === 'Enter') {
                      handleUpdatePassword();
                    }
                  }}
                  className="bg-gray-100 flex-grow"
                  type={`${showPassword ? 'text' : 'password'}`}
                  placeholder="Password"
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  onClick={() => {
                    setShowPassword(!showPassword);
                    setTimeout(() => {
                      setShowPassword(false);
                    }, 1000);
                  }}
                >
                  {showPassword ? (
                    <EyeIcon className="text-gray-600 -mt-1" />
                  ) : (
                    <div>
                      <EditHideIcon wrapperClass="text-gray-600 mt-3" />
                    </div>
                  )}
                </button>
              </div>

              {incorrectPassword && (
                <ErrorContent className="mb-16 -ml-4">
                  Whoops, incorrect password! Try again
                </ErrorContent>
              )}
              <div className="flex justify-end mt-10">
                <Button
                  className="bg-blue-800 text-white border rounded-sm px-24 py-13"
                  onClick={handleUpdatePassword}
                  disabled={isSubmitDisabled}
                >
                  Enter password
                </Button>
              </div>
            </ModalContent>
          </Modal>

          <div
            id="public-file-wrapper"
            className="overflow-scroll"
            style={{ height: 'calc(100vh)' }}
          >
            <Header />

            {publicFileStatus === 'inactive' ? (
              <div className="h-full bg-gray-100 py-64">
                <div className="h-284 mx-64 mt-32">
                  <EmptyStateDashboard
                    contents={[
                      'Oops, this file is not available to view publicly.',
                      'Contact the owner of the file if you think this is a mistake.',
                    ]}
                    src={EMPTY_STATE_IMAGES.lessonCreation}
                  />
                </div>
              </div>
            ) : publicFileStatus === 'unavailable' ? (
              <div className="h-full bg-gray-100 py-64">
                <div className="h-284 mx-64 mt-32">
                  <EmptyStateDashboard
                    contents={[
                      'Oops, this file is not available',
                      'Contact the owner of the file if you think this is a mistake.',
                    ]}
                    src={EMPTY_STATE_IMAGES.lessonCreation}
                  />
                </div>
              </div>
            ) : currentFile?.authorisation ? (
              <PageErrorBoundary wrapperClass="mt-32">
                <div className=" bg-gray-100 pt-64">
                  {!viewsWithoutFileHeader.includes(router.query.type as string) &&
                    currentFile?.coverImage &&
                    currentFile?.showCover && (
                      <div className="p-10 bg-white shadow">
                        <img
                          style={{ height: '23vh' }}
                          className="w-full object-center object-cover rounded-xl h-full"
                          loading="lazy"
                          src={currentFile.coverImage}
                        />
                      </div>
                    )}
                  <PublicFileViewNavbar />
                  <PageErrorBoundary>
                    <div className="">
                      {activeInternalFile({
                        id: router.query.view as string,
                        type: router.query.type as string,
                      })}
                    </div>
                  </PageErrorBoundary>
                </div>
              </PageErrorBoundary>
            ) : (
              <></>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
